/**
 * lib/store/auth-slice.ts — Stage FW-1 (Frontend Wiring 1: Auth)
 *
 * In-memory auth state for JWT tokens + logged-in user. This state is NOT
 * persisted to localStorage (deliberate — VAPT requirement: no tokens in
 * localStorage).
 *
 * Session restoration after page reload:
 * - In-memory tokens are lost on reload
 * - BUT the Django backend sets an httpOnly refresh cookie on login
 * - AuthGuard (FW-3) attempts cookie-based refresh on mount before redirecting
 * - See lib/api/client.ts:71-93 (performRefresh) and
 *   components/layout/AuthGuard.tsx:39-45 for the restoration flow
 *
 * Tokens are stored in Zustand memory only. The API client (lib/api/client.ts)
 * reads these tokens to inject Bearer headers and handle refresh flow.
 */

import { create } from 'zustand';
import type { User } from '@/lib/types';
import { setSentryUser, clearSentryUser } from '@/lib/sentry';
import { clearLocalStorage } from './persistence';

export interface AuthState {
  /** JWT access token (in memory only, not persisted) */
  accessToken: string | null;
  /** JWT refresh token (in memory only, not persisted) */
  refreshToken: string | null;
  /** Currently logged-in user (from /auth/me/ endpoint) */
  user: User | null;
  /** Derived: true if we have an access token */
  isAuthenticated: boolean;
}

export interface AuthActions {
  /** Store tokens + user after successful login */
  setAuth: (data: { access: string; refresh: string; user: User }) => void;
  /** Clear all auth state (logout) */
  clearAuth: () => void;
  /** Update tokens after refresh (user stays the same) */
  updateTokens: (data: { access: string; refresh: string }) => void;
}

const initialState: AuthState = {
  accessToken: null,
  refreshToken: null,
  user: null,
  isAuthenticated: false,
};

/**
 * Auth store — IN MEMORY ONLY. No persist middleware.
 * Tokens are wiped on hard reload (by design).
 */
export const useAuthStore = create<AuthState & AuthActions>((set) => ({
  ...initialState,

  setAuth: ({ access, refresh, user }) => {
    // Set Sentry user context for error tracking
    if (user) {
      setSentryUser({
        id: user.id,
        email: user.email,
        role: user.role,
      });
    }
    set({
      accessToken: access,
      refreshToken: refresh,
      user,
      isAuthenticated: true,
    });
  },

  clearAuth: () => {
    // Clear Sentry user context on logout
    clearSentryUser();
    // Purge persisted preferences from localStorage
    clearLocalStorage();
    set(initialState);
  },

  updateTokens: ({ access, refresh }) =>
    set((state) => ({
      accessToken: access,
      refreshToken: refresh,
      // Keep user as-is; only tokens rotate
      user: state.user,
      isAuthenticated: true,
    })),
}));
